import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import { Mail, ListTodo, CheckCircle2, StickyNote, FileText, Heart, Send, Trash2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND = {
  email: { icon: Mail, cls: "bg-red-50 text-red-500", label: "Email" },
  task: { icon: ListTodo, cls: "bg-violet-50 text-violet-600", label: "Task" },
  task_done: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-600", label: "Task" },
  note: { icon: StickyNote, cls: "bg-amber-50 text-amber-600", label: "Nota" },
  contract: { icon: FileText, cls: "bg-sky-50 text-sky-600", label: "Contratto" },
  health: { icon: Heart, cls: "bg-pink-50 text-pink-600", label: "Health" },
};
const FILTERS = [["", "Tutto"], ["email", "Email"], ["task", "Task"], ["note", "Note"], ["contract", "Contratti"], ["health", "Health"]];

const fmt = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};

const TimelineItem = ({ e, onDeleteNote }) => {
  const k = KIND[e.kind] || KIND.note;
  const Icon = k.icon;
  return (
    <div className="relative flex gap-3 pb-4" data-testid={`timeline-item-${e.kind}`}>
      <div className={`z-10 h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${k.cls}`}><Icon className="h-3.5 w-3.5" /></div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{e.title}</span>
          {e.meta?.urgent && <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />}
          <span className="ml-auto text-[11px] text-muted-foreground shrink-0">{fmt(e.date)}</span>
        </div>
        {e.detail && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{e.detail}</p>}
        {e.kind === "note" && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
            <span>{e.meta.author_name}</span>
            <button className="text-red-400 hover:text-red-600" onClick={() => onDeleteNote(e.meta.note_id)} data-testid={`timeline-note-delete-${e.meta.note_id}`}><Trash2 className="h-3 w-3" /></button>
          </div>
        )}
        {e.kind === "task" && <p className="text-[11px] text-muted-foreground">{[e.meta.assignee_name, e.meta.due_date && `scad. ${fmt(e.meta.due_date)}`].filter(Boolean).join(" · ")}</p>}
      </div>
    </div>
  );
};

export const ClientTimeline = ({ clientId }) => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/csm/clients/${clientId}/timeline`);
      setEvents(r.data.events || []);
    } catch { toast.error("Errore caricamento timeline"); }
    setLoading(false);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${API}/csm/clients/${clientId}/notes`, { text: note });
      setNote("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const deleteNote = async (id) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    try { await axios.delete(`${API}/csm/notes/${id}`); load(); } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const shown = filter ? events.filter(e => e.kind === filter || (filter === "task" && e.kind === "task_done")) : events;

  return (
    <div className="space-y-3" data-testid="client-timeline">
      <div className="flex gap-2">
        <textarea className="flex min-h-[38px] flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" rows={1} placeholder="Aggiungi una nota (es. esito chiamata)..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="timeline-note-input" />
        <Button size="sm" className="h-auto gap-1" onClick={addNote} disabled={!note.trim()} data-testid="timeline-note-add"><Send className="h-3.5 w-3.5" /> Aggiungi</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`timeline-filter-${k || "all"}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${filter === k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>{l}</button>
        ))}
      </div>
      <div className="relative max-h-[360px] overflow-y-auto pr-1">
        <div className="absolute left-[13px] top-1 bottom-1 w-px bg-border" />
        {loading ? <p className="text-xs text-muted-foreground py-4">Caricamento...</p>
          : shown.length === 0 ? <p className="text-xs text-muted-foreground italic py-4 pl-10" data-testid="timeline-empty">Nessun evento</p>
          : shown.map((e, i) => <TimelineItem key={i} e={e} onDeleteNote={deleteNote} />)}
      </div>
    </div>
  );
};
